import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';
import type { AcceptanceMethod, AcceptanceResult, PendingDocument, PublicDocument } from './types';

/**
 * INTERIM data layer for the player documents endpoints.
 *
 * The api ships these routes with `@ApiOkResponse` annotations, so once it is
 * reachable, `yarn generate-api` will emit `useDocumentsControllerGetPending`,
 * `useDocumentsControllerAccept`, etc. in `src/lib/endpoints.ts`. Replace the
 * hooks below with the generated ones at that point — they funnel through the
 * same `useCustomInstance` mutator, so the swap is mechanical.
 */

export const documentsPendingQueryKey = (): [string] => ['/documents/pending'];

/** Documents the current user must still accept. */
export function useDocumentsPending(
  enabled: boolean,
  options?: { staleTime?: number; refetchOnMount?: boolean | 'always' },
) {
  const request = useCustomInstance<PendingDocument[]>();
  return useQuery({
    queryKey: documentsPendingQueryKey(),
    queryFn: () => request({ url: '/documents/pending', method: 'GET' }),
    enabled,
    staleTime: options?.staleTime ?? 60_000,
    refetchOnMount: options?.refetchOnMount,
  });
}

/** Current published version of a document by key (public/pre-login). */
export function useDocumentPublic(key: string, enabled = true) {
  const request = useCustomInstance<PublicDocument>();
  return useQuery({
    queryKey: ['/documents', key, 'public'],
    queryFn: () => request({ url: `/documents/${key}/public`, method: 'GET' }),
    enabled,
  });
}

/**
 * Accept the current published version of a document.
 *
 * `method` records where consent was captured (signup funnel vs. standalone
 * gate vs. settings); it defaults to `gate` server-side when omitted.
 *
 * On success we *optimistically prune* the accepted version from the shared
 * `/documents/pending` cache before invalidating. This is what stops the
 * `TermsGate` from flashing a just-accepted HARD doc in the gap between signup
 * completing (which closes the sheet and un-suppresses the gate) and the
 * invalidation refetch resolving. Without the prune, the gate re-renders
 * against the stale, still-non-empty cache and flickers a modal.
 */
export function useAcceptDocument() {
  const request = useCustomInstance<AcceptanceResult>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { key: string; documentVersionId: string; method?: AcceptanceMethod }) =>
      request({
        url: `/documents/${vars.key}/accept`,
        method: 'POST',
        data: { documentVersionId: vars.documentVersionId, ...(vars.method ? { method: vars.method } : {}) },
      }),
    onSuccess: (_result, vars) => {
      queryClient.setQueryData<PendingDocument[]>(documentsPendingQueryKey(), (prev) =>
        prev ? prev.filter((doc) => doc.documentVersionId !== vars.documentVersionId) : prev,
      );
      queryClient.invalidateQueries({ queryKey: documentsPendingQueryKey() });
    },
  });
}
